/**
 * StammdatenEditModal - Inline-Bearbeitung aller Stammdaten eines Klienten
 */
import { useState } from 'react';
import { Save, X, User, MapPin, Phone, Mail, Calendar, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { betreuungAPI } from '@/api/betreuungAPI';
import { WOHNFORM_OPTIONS } from '@/lib/wohnform';


const PFLEGEGRAD_OPTIONS = [
  { value: 'keiner', label: 'Kein Pflegegrad' },
  { value: '1', label: 'Pflegegrad 1' },
  { value: '2', label: 'Pflegegrad 2' },
  { value: '3', label: 'Pflegegrad 3' },
  { value: '4', label: 'Pflegegrad 4' },
  { value: '5', label: 'Pflegegrad 5' },
];

const STATUS_OPTIONS = [
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'ruhend', label: 'Ruhend' },
  { value: 'beendet', label: 'Beendet' },
];

const PRIORITAET_OPTIONS = [
  { value: 'niedrig', label: 'Niedrig' },
  { value: 'mittel', label: 'Mittel' },
  { value: 'hoch', label: 'Hoch' },
  { value: 'dringend', label: 'Dringend' },
];

export default function StammdatenEditModal({ open, onClose, klient, klientId, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vorname: klient?.vorname || '',
    nachname: klient?.nachname || '',
    geburtsdatum: klient?.geburtsdatum ? klient.geburtsdatum.split('T')[0] : '',
    geburtsort: klient?.geburtsort || '',
    telefon: klient?.telefon || '',
    email: klient?.email || '',
    adresse: klient?.adresse || '',
    plz: klient?.plz || '',
    ort: klient?.ort || '',
    wohnform: klient?.wohnform || 'nicht_angegeben',
    pflegegrad: klient?.pflegegrad || 'keiner',
    status: klient?.status || 'aktiv',
    prioritaet: klient?.prioritaet || 'mittel',
    fallnummer: klient?.fallnummer || '',
    krankenversicherung: klient?.krankenversicherung || '',
    haupteinnahmequelle: klient?.haupteinnahmequelle || '',
    diagnosen: klient?.diagnosen || '',
  });

  if (!open) return null;

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.vorname.trim() || !form.nachname.trim()) {
      toast.error('Vorname und Nachname sind Pflichtfelder');
      return;
    }

    setSaving(true);
    try {
      const updateData = { ...form };
      // Geburtsdatum nur senden wenn vorhanden
      if (!updateData.geburtsdatum) delete updateData.geburtsdatum;
      
      await betreuungAPI.updateKlient(klientId, updateData);
      toast.success('Stammdaten gespeichert');
      if (onSaved) onSaved(updateData);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="stammdaten-edit-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white rounded-t-xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Pencil className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Stammdaten bearbeiten</h2>
              <p className="text-xs text-slate-500">{klient?.vorname} {klient?.nachname}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="stammdaten-close-btn">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Persönliche Daten */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" /> Persönliche Daten
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vorname *</Label>
                <Input value={form.vorname} onChange={e => updateField('vorname', e.target.value)} data-testid="stamm-edit-vorname" />
              </div>
              <div>
                <Label className="text-xs">Nachname *</Label>
                <Input value={form.nachname} onChange={e => updateField('nachname', e.target.value)} data-testid="stamm-edit-nachname" />
              </div>
              <div>
                <Label className="text-xs">Geburtsdatum</Label>
                <Input type="date" value={form.geburtsdatum} onChange={e => updateField('geburtsdatum', e.target.value)} data-testid="stamm-edit-geburtsdatum" />
              </div>
              <div>
                <Label className="text-xs">Geburtsort</Label>
                <Input value={form.geburtsort} onChange={e => updateField('geburtsort', e.target.value)} data-testid="stamm-edit-geburtsort" />
              </div>
            </div>
          </div>

          <Separator />

          {/* Status & Fallnummer */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Status & Verwaltung
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => updateField('status', v)}>
                  <SelectTrigger data-testid="stamm-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priorität</Label>
                <Select value={form.prioritaet} onValueChange={v => updateField('prioritaet', v)}>
                  <SelectTrigger data-testid="stamm-edit-prioritaet"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITAET_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fallnummer / Az.</Label>
                <Input value={form.fallnummer} onChange={e => updateField('fallnummer', e.target.value)} data-testid="stamm-edit-fallnummer" />
              </div>
            </div>
          </div>

          <Separator />

          {/* Kontakt & Adresse */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Kontakt & Adresse
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input value={form.telefon} onChange={e => updateField('telefon', e.target.value)} data-testid="stamm-edit-telefon" />
              </div>
              <div>
                <Label className="text-xs">E-Mail</Label>
                <Input value={form.email} onChange={e => updateField('email', e.target.value)} data-testid="stamm-edit-email" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Adresse / Straße</Label>
                <Input value={form.adresse} onChange={e => updateField('adresse', e.target.value)} data-testid="stamm-edit-adresse" />
              </div>
              <div>
                <Label className="text-xs">PLZ</Label>
                <Input value={form.plz} onChange={e => updateField('plz', e.target.value)} data-testid="stamm-edit-plz" />
              </div>
              <div>
                <Label className="text-xs">Ort</Label>
                <Input value={form.ort} onChange={e => updateField('ort', e.target.value)} data-testid="stamm-edit-ort" />
              </div>
            </div>
          </div>

          <Separator />

          {/* Lebenssituation */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Lebenssituation
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Wohnform</Label>
                <Select value={form.wohnform} onValueChange={v => updateField('wohnform', v)}>
                  <SelectTrigger data-testid="stamm-edit-wohnform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WOHNFORM_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pflegegrad</Label>
                <Select value={form.pflegegrad} onValueChange={v => updateField('pflegegrad', v)}>
                  <SelectTrigger data-testid="stamm-edit-pflegegrad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PFLEGEGRAD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Krankenversicherung</Label>
                <Input value={form.krankenversicherung} onChange={e => updateField('krankenversicherung', e.target.value)} data-testid="stamm-edit-kv" />
              </div>
              <div>
                <Label className="text-xs">Haupteinnahmequelle</Label>
                <Input value={form.haupteinnahmequelle} onChange={e => updateField('haupteinnahmequelle', e.target.value)} data-testid="stamm-edit-einnahme" />
              </div>
            </div>
          </div>

          <Separator />

          {/* Diagnosen */}
          <div>
            <Label className="text-xs">Diagnosen</Label>
            <Textarea
              value={form.diagnosen}
              onChange={e => updateField('diagnosen', e.target.value)}
              rows={3}
              placeholder="ICD-Codes und Beschreibungen..."
              data-testid="stamm-edit-diagnosen"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl sticky bottom-0">
          <Button variant="outline" onClick={onClose} data-testid="stamm-edit-cancel">Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="stamm-edit-save">
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                Speichere...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Save className="w-4 h-4" /> Speichern</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
